import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './MobileMenu.css';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  label: string;
  to?: string;
  children?: MenuItem[];
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

  const menuItems: MenuItem[] = useMemo(
    () => [
      { label: 'Home', to: '/' },
      { label: 'About Us', to: '/about' },
      {
        label: 'Facilities',
        children: [
          {
            label: 'Bar & Restaurant',
            children: [
              {
                label: 'Restaurant',
                children: [
                  { label: 'Hangout', to: '/hangout' },
                  { label: 'Restro', to: '/restaurant' },
                  { label: 'Pub', to: '/pub' },
                  { label: 'Celebrity Appearance', to: '/celebrity-appearance' }
                ]
              },
              { label: 'Bar & Lounge', to: '/bar-and-lounge' }
            ]
          },
          {
            label: 'Lounge',
            children: [
              { label: 'Family Lounge', to: '/family-lounge' },
              { label: 'Corporate / Business Lounge', to: '/corporate-business-lounge' }
            ]
          },
          {
            label: 'Club',
            children: [
              { label: 'Night Club', to: '/night-club' },
              { label: 'Pub', to: '/pub' },
              { label: 'Night life', to: '/night-life' }
            ]
          },
          {
            label: 'Event',
            children: [
              { label: 'Corporate Event', to: '/corporate-event' },
              { label: 'Family Event', to: '/family-event' },
              { label: 'Filmshoot Event', to: '/filmshoot-event' }
            ]
          },
          {
            label: 'Party',
            children: [
              { label: 'Corporate Party', to: '/corporate-party' },
              { label: 'Filmshoot Party', to: '/filmshoot-party' },
              { label: 'Kitty Party', to: '/kitty-party' },
              { label: 'Birthday Party', to: '/birthday-party' },
              { label: 'Prewedding Shoot', to: '/prewedding-shoot' },
              { label: 'Family Parties', to: '/family-parties' }
            ]
          }
        ]
      },
      { label: 'Menu', to: '/menu' },
      { label: 'Event & media', to: '/events-and-media' },
      { label: 'Contact Us', to: '/contact' },
      { label: 'Enquiry', to: '/enquiry' }
    ],
    []
  );

  const toggleSubmenu = (menuKey: string) => {
    const newMenus = new Set(expandedMenus);
    if (newMenus.has(menuKey)) {
      newMenus.delete(menuKey);
    } else {
      newMenus.add(menuKey);
    }
    setExpandedMenus(newMenus);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen, onClose]);

  const renderMenuItems = (items: MenuItem[], level = 0, parentKey = 'root') => {
    return (
      <ul className={level === 0 ? 'mobile-menu-level' : 'submenu'}>
        {items.map((item) => {
          const menuKey = `${parentKey}-${item.label}`;
          const hasChildren = Boolean(item.children?.length);
          const isExpanded = expandedMenus.has(menuKey);

          return (
            <li key={menuKey} className={hasChildren ? 'menu-item-with-submenu' : ''}>
              {hasChildren ? (
                <>
                  <button
                    type="button"
                    className="menu-item-header"
                    onClick={() => toggleSubmenu(menuKey)}
                    aria-expanded={isExpanded}
                  >
                    <span>{item.label}</span>
                    <i className={`fas fa-chevron-down ${isExpanded ? 'active' : ''}`}></i>
                  </button>
                  {isExpanded && renderMenuItems(item.children || [], level + 1, menuKey)}
                </>
              ) : (
                <Link to={item.to || '#'} onClick={onClose}>
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      <div className={`mobile-menu-overlay ${isOpen ? 'active' : ''}`} onClick={onClose} />
      <aside id="mobile-navigation" className={`mobile-menu-container ${isOpen ? 'active' : ''}`} aria-hidden={!isOpen}>
        <div className="mobile-menu-header">
          <button className="mobile-menu-close" onClick={onClose} aria-label="Close menu" type="button">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <nav className="mobile-menu-nav">
          {renderMenuItems(menuItems)}
        </nav>
      </aside>
    </>
  );
};

export default MobileMenu;
